import { useState, useCallback, useEffect, useContext } from 'react';
import { useAPI } from '../../../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../../../util/toasts';
import { useCategoryApiV1 } from '../../../../../clients/quipli-service/category/category-api-v1';
import { Category } from '../../../../../clients/quipli-service/category/category';
import { Product } from '../../../../../clients/quipli-service/product/product';
import { TenantContext } from '../../../../../context/tenant-context';

export const useAddTagData = (id?: string) => {
  const { contextPath } = useContext(TenantContext);

  // States
  const [name, setName] = useState<string>('');
  const [discount, setDiscount] = useState<number>(0);
  const [rentalLimit, setRentalLimit] = useState<number>();
  const [productAndCategorySpecific, setProductAndCategorySpecific] = useState<boolean>(false);
  const [products, setProducts] = useState<number[]>([]);
  const [categories, setCategories] = useState<number[]>([]);

  // Loading states
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isInitiallyLoading, setIsInitiallyLoading] = useState<boolean>(true);

  // API calls
  const { getTagPricing, listProducts, upsertTagPricing } = useAPI();
  const { listCategories } = useCategoryApiV1();
  const [allProducts, setAllProducts] = useState<Product[]>();
  const [allCategories, setAllCategories] = useState<Category[]>();

  // Only fetch products and categories if they are not already fetched and needed
  useEffect(() => {
    const getContent = async () => {
      try {
        setIsSaving(true);
        const [products, categories] = await Promise.all([listProducts(), listCategories()]);
        setAllProducts(products);
        setAllCategories(categories);
      } catch {
        toast('Failed to load products and categories.', 'error');
      } finally {
        setIsInitiallyLoading(false);
        setIsSaving(false);
      }
    };

    if (productAndCategorySpecific && allProducts === undefined && allCategories === undefined) {
      getContent();
    }
  }, [productAndCategorySpecific, allProducts, allCategories]);

  // Fetch tag pricing
  const fetchTagPricing = useCallback(async () => {
    if (id) {
      const tagPricing = await getTagPricing(id);
      setName(tagPricing.name);
      setDiscount(tagPricing.discount);
      setRentalLimit(tagPricing.rentalLimit ?? undefined);
      setProductAndCategorySpecific(tagPricing.productIds.length + tagPricing.categoryIds.length > 0);
      setProducts(tagPricing.productIds);
      setCategories(tagPricing.categoryIds);
    }
  }, [id, getTagPricing]);

  const initLoading = async () => {
    try {
      setIsInitiallyLoading(true);
      await fetchTagPricing();
    } catch {
      toast('Failed to load tag pricing.', 'error');
    } finally {
      setIsInitiallyLoading(false);
    }
  };

  useEffect(() => {
    initLoading();
  }, []);

  // Save function that accepts values directly to avoid race conditions
  const save = useCallback(
    async (values?: {
      name?: string;
      discount?: number;
      rentalLimit?: number | null; // null means explicitly clear
      products?: number[];
      categories?: number[];
      // When true, allow save even if there's no id (create flow explicit save button)
      forceSave?: boolean;
    }) => {
      // Do not autosave on edits during create flow (no id) unless explicitly forced
      if (!id && !values?.forceSave) {
        return;
      }

      setIsSaving(true);
      try {
        // Handle rentalLimit explicitly - if it's null, use undefined; if it's undefined, use current state
        let finalRentalLimit: number | undefined;
        if (values?.rentalLimit === null) {
          finalRentalLimit = undefined;
        } else if (values?.rentalLimit !== undefined) {
          finalRentalLimit = values.rentalLimit;
        } else {
          finalRentalLimit = rentalLimit;
        }

        const newTagPricing = await upsertTagPricing({
          id: id ? parseInt(id, 10) : undefined,
          name: values?.name ?? name,
          discount: values?.discount ?? discount,
          rentalLimit: finalRentalLimit,
          productIds: values?.products ?? products,
          categoryIds: values?.categories ?? categories,
          isDeleted: false,
        });

        if (!id) {
          toast(`Tag created successfully.`, 'success');

          // Annoying, but routing to the edit page after showing the success for a moment
          // would rather a full-page spinner but we don't have that pattern used yet
          await new Promise((resolve) => setTimeout(resolve, 500));

          const url = `${contextPath}manager/tagpricing/form/${newTagPricing.id}`;
          window.open(url, '_self')?.focus();
        }
      } catch (error) {
        toast('Failed to save', 'error');
      } finally {
        setIsSaving(false);
      }
    },
    [id, name, discount, rentalLimit, products, categories, upsertTagPricing],
  );

  return {
    // State
    name,
    discount,
    rentalLimit,
    productAndCategorySpecific,
    products,
    categories,
    isSaving,
    isInitiallyLoading,

    // Simple setters
    setName,
    setDiscount,
    setRentalLimit,
    setProductAndCategorySpecific,
    setProducts,
    setCategories,

    // Save function
    save,

    // Manual setters (if needed)
    setIsInitiallyLoading,

    allProducts: allProducts ?? [],
    allCategories: allCategories ?? [],
  };
};
